<script lang="ts" setup>
import { ref, watch, onMounted, onUnmounted, computed, nextTick } from 'vue';
import { useForm } from 'vee-validate';
import gsap from 'gsap';
import FieldRenderer from './FieldRenderer.vue';
import type { FormField } from './types/formTypeStructure';
import { Eye, Edit, Plus } from 'lucide-vue-next';

interface Props {
  field: FormField;
  formValues: Record<string, any>;
  currentLang?: string;
}

const props = defineProps<Props>();

const { values, setFieldValue } = useForm();

const isModalOpen = ref(false);
const isViewMode = ref(false);
const expandedSeries = ref<Set<number>>(new Set());

const modalRef = ref<HTMLElement | null>(null);
const overlayRef = ref<HTMLElement | null>(null);
const contentRef = ref<HTMLElement | null>(null);
const seriesRefs = ref<Record<number, HTMLElement | null>>({});

const repeatGroup = props.field.repeatGroup!;
const basedOnValue = computed(() => Number(values[props.field.fieldName]) || 0);
const filledSeries = computed(() => props.formValues?.[repeatGroup.fieldName] ?? []);

const hasFilledData = computed(() => {
  return filledSeries.value.some((group: any) =>
    Object.values(group || {}).some((v) => v !== '' && v !== null && v !== undefined)
  );
});

watch(() => values[props.field.fieldName], (newVal) => {
  const currentValue = Number(newVal) || 0;
  if (currentValue > 0 && !hasFilledData.value && !isModalOpen.value) {
    setTimeout(() => {
      openModal(false);
    }, 300);
  } else if (currentValue === 0) {
    closeModal();
  }
});

function openModal(view = false) {
  isViewMode.value = view;
  isModalOpen.value = true;
  nextTick(() => {
    if (modalRef.value && overlayRef.value && contentRef.value) {
      gsap.set(modalRef.value, { display: 'flex' });
      gsap.set(overlayRef.value, { opacity: 0 });
      gsap.set(contentRef.value, { scale: 0.9, opacity: 0, y: 20 });
      const tl = gsap.timeline();
      tl.to(overlayRef.value, { opacity: 1, duration: 0.15, ease: 'power2.out' })
        .to(contentRef.value, {
          scale: 1,
          opacity: 1,
          y: 0,
          duration: 0.2,
          ease: 'power2.out',
        }, '-=0.05');
    }
  });
}

function closeModal() {
  if (!modalRef.value || !overlayRef.value || !contentRef.value) return;
  const tl = gsap.timeline({
    onComplete: () => {
      isModalOpen.value = false;
      isViewMode.value = false;
      expandedSeries.value = new Set();
    }
  });
  tl.to(contentRef.value, {
    scale: 0.9,
    opacity: 0,
    y: 20,
    duration: 0.15,
    ease: 'power2.in',
  }).to(overlayRef.value, {
    opacity: 0,
    duration: 0.1,
    ease: 'power2.in',
  }, '-=0.05').set(modalRef.value, { display: 'none' });
}

function openEdit() {
  prefillTempFields(filledSeries.value);
  openModal(false);
}

function openView() {
  prefillTempFields(filledSeries.value);
  openModal(true);
}

function prefillTempFields(series: any[]) {
  for (let i = 0; i < basedOnValue.value; i++) {
    const groupData = series[i] ?? {};
    repeatGroup.formFields.forEach((subField) => {
      const key = `${repeatGroup.fieldName}_${subField.fieldName}_${i}`;
      setFieldValue(key, groupData[subField.fieldName] ?? '');
    });
  }
}

function saveGroups() {
  const groups: Record<string, any>[] = [];
  for (let i = 0; i < basedOnValue.value; i++) {
    const group: Record<string, any> = {};
    repeatGroup.formFields.forEach((subField) => {
      const key = `${repeatGroup.fieldName}_${subField.fieldName}_${i}`;
      group[subField.fieldName] = values[key];
      setFieldValue(key, '');
    });
    groups.push(group);
  }
  setFieldValue(repeatGroup.fieldName, groups);
  closeModal();
}

onMounted(() => {
  document.body.style.overflow = isModalOpen.value ? 'hidden' : '';
});
onUnmounted(() => {
  document.body.style.overflow = '';
});
</script>

<template>
  <div class="space-y-6">
    <FieldRenderer
      :field="field"
      :currentLang="currentLang"
      :formValues="formValues"
      :showControlButtons="true"
      :hasFilledData="hasFilledData"
      @onOpenModal="() => openModal(false)"
      @onOpenEdit="openEdit"
      @onOpenView="openView"
    />

    <!-- Modal section (à compléter plus tard) -->
    <!-- On déportera la modale dans un composant : RepeatGroupModal.vue -->
    <div ref="modalRef" style="display: none"></div>
  </div>
</template>

<style scoped>
/* Ajoute ton style plus tard ici si besoin */
</style>
